import type { TableColumns } from '@ohpen/enso-ui';
import { FeeDefinition, FeeBand, FieldType, FeeProfile, StringPair } from '@pmp/api';
import { linkedToActiveTodayColumn, withSourceDecimals } from '@pmp/utils';

export type FeesExport = {
  feeId?: number;
  calculationType?: StringPair | null;
  feeType?: StringPair | null;
  feeName?: string | null;
  amount?: number | null;
  percentage?: number | null;
  bandParameter?: StringPair | null;
  bandIndex?: number;
  bandBaseTopAmount?: number | null;
  bandBaseBottomAmount?: number | null;
  bandAmount?: number | null;
};

const feesDataColumns: TableColumns<FeeDefinition> = [
  {
    title: 'pmp.fees.labels.feeId',
    dataIndex: 'feeId',
    key: 'feeId',
    width: 120,
    options: {
      filter: 'search',
      sorter: true,
      hide: true,
    },
  },
  {
    title: 'pmp.fees.labels.feeName',
    dataIndex: 'feeName',
    key: 'feeName',
    options: {
      filter: 'search',
      sorter: true,
    },
    width: 250,
  },
  {
    title: 'pmp.fees.labels.description',
    dataIndex: 'description',
    key: 'description',
    options: {
      filter: 'search',
      sorter: true,
    },
    width: 210,
  },
  {
    title: 'pmp.fees.labels.feeTypeId',
    dataIndex: 'feeType',
    key: 'feeTypeId',
    options: {
      format: {
        type: 'valueListKey',
      },
      filter: 'select',
      listName: FieldType.FEE_TYPE,
      hide: true,
    },
    width: 100,
  },
  {
    title: 'pmp.fees.labels.feeType',
    dataIndex: 'feeType',
    key: 'feeType',
    options: {
      format: {
        type: 'valueList',
      },
      filter: 'select',
      listName: FieldType.FEE_TYPE,
    },
    width: 130,
  },
  {
    title: 'pmp.fees.labels.calculationTypeId',
    dataIndex: 'calculationType',
    key: 'calculationTypeId',
    options: {
      format: {
        type: 'valueListKey',
      },
      filter: 'select',
      listName: FieldType.CALCULATION_TYPE,
      hide: true,
    },
    width: 100,
  },
  {
    title: 'pmp.fees.labels.calculationType',
    dataIndex: 'calculationType',
    key: 'calculationType',
    options: {
      format: {
        type: 'valueList',
      },
      filter: 'select',
      listName: FieldType.CALCULATION_TYPE,
    },
    width: 160,
  },
  {
    title: 'pmp.fees.labels.amount',
    dataIndex: 'amount',
    key: 'amount',
    options: {
      filter: 'number',
      sorter: true,
      format: {
        type: 'currency',
      },
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.percentage',
    dataIndex: 'percentage',
    key: 'percentage',
    options: {
      filter: 'number',
      sorter: true,
      format: {
        type: 'percentage',
      },
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.basedOnId',
    dataIndex: 'basedOn',
    key: 'basedOnId',
    options: {
      format: {
        type: 'valueListKey',
      },
      filter: 'select',
      listName: FieldType.PRODUCT_FEE_BASED_ON,
      hide: true,
      hideInForm: true,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.basedOn',
    dataIndex: 'basedOn',
    key: 'basedOn',
    options: {
      format: {
        type: 'valueList',
      },
      filter: 'select',
      listName: FieldType.PRODUCT_FEE_BASED_ON,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.paymentMethodIds',
    dataIndex: 'paymentMethods',
    key: 'paymentMethodIds',
    options: {
      format: {
        type: 'multiValueListKey',
      },
      filter: 'select',
      listName: FieldType.PAYMENT_METHOD,
      hide: true,
      hideInForm: true,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.paymentMethods',
    dataIndex: 'paymentMethods',
    key: 'paymentMethods',
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.PAYMENT_METHOD,
    },
    width: 370,
  },
  {
    title: 'pmp.fees.labels.paymentStageIds',
    dataIndex: 'paymentStages',
    key: 'paymentStageIds',
    options: {
      format: {
        type: 'multiValueListKey',
      },
      filter: 'select',
      listName: FieldType.PAYMENT_STAGE,
      hide: true,
      hideInForm: true,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.paymentStages',
    dataIndex: 'paymentStages',
    key: 'paymentStages',
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.PAYMENT_STAGE,
    },
    width: 180,
  },
  {
    title: 'pmp.fees.labels.accountCode',
    dataIndex: 'accountCode',
    key: 'accountCode',
    options: {
      filter: 'search',
      sorter: true,
    },
    width: 170,
  },
  {
    title: 'pmp.fees.labels.costCentre',
    dataIndex: 'costCentre',
    key: 'costCentre',
    options: {
      filter: 'search',
      sorter: true,
    },
    width: 160,
  },
  {
    title: 'pmp.fees.labels.refundable',
    dataIndex: 'refundable',
    key: 'refundable',
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.waivable',
    dataIndex: 'waivable',
    key: 'waivable',
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.estimated',
    dataIndex: 'estimated',
    key: 'estimated',
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
    width: 150,
  },
];

const feesActionsColumn: TableColumns<FeeDefinition>[number] = {
  title: 'enso.labels.actions',
  dataIndex: '',
  key: 'actions',
  width: 100,
  fixed: 'right',
};

export const feesColumns: TableColumns<FeeDefinition> = [...feesDataColumns, feesActionsColumn];

export const feesListColumns: TableColumns<FeeDefinition> = [
  ...feesDataColumns,
  {
    title: 'pmp.fees.labels.feeProfileIds',
    dataIndex: 'feeProfiles',
    key: 'feeProfileIds',
    options: {
      format: {
        type: 'multiValueListKey',
      },
      filter: 'select',
      listName: FieldType.FEE_PROFILES,
      hide: true,
    },
    width: 150,
  },
  {
    title: 'pmp.fees.labels.feeProfiles',
    dataIndex: 'feeProfiles',
    key: 'feeProfiles',
    options: {
      format: {
        type: 'multiValueList',
      },
      allowSearch: true,
      filter: 'select',
      listName: FieldType.FEE_PROFILES,
    },
    width: 250,
  },
  linkedToActiveTodayColumn<FeeDefinition>('pmp.fees.labels.inFeeProfileOfActiveToday', 300),
  feesActionsColumn,
];

export const bands: TableColumns<FeeBand> = [
  {
    title: 'pmp.fees.labels.baseBottomAmount',
    dataIndex: 'baseBottomAmount',
    key: 'baseBottomAmount',
    width: 160,
    options: {
      filter: 'number',
      sorter: true,
      format: {
        type: 'currency',
        decimalScale: 0,
      },
    },
  },
  {
    title: 'pmp.fees.labels.baseTopAmount',
    dataIndex: 'baseTopAmount',
    key: 'baseTopAmount',
    width: 160,
    options: {
      filter: 'number',
      sorter: true,
      format: {
        type: 'currency',
        decimalScale: 0,
      },
    },
  },
  {
    title: 'pmp.fees.labels.amount',
    dataIndex: 'amount',
    key: 'amount',
    width: 150,
    options: {
      filter: 'number',
      sorter: true,
      format: {
        type: 'currency',
      },
    },
  },
];

const feesFlatColumnsBase: TableColumns<FeeDefinition> = [
  {
    title: 'pmp.fees.labels.feeId',
    dataIndex: 'feeId',
    key: 'feeId',
    width: 120,
  },
  {
    title: 'pmp.fees.labels.feeName',
    dataIndex: 'feeName',
    key: 'feeName',
    width: 250,
  },
  {
    title: 'pmp.fees.labels.description',
    dataIndex: 'description',
    key: 'description',
    width: 210,
  },
  {
    title: 'pmp.fees.labels.feeType',
    dataIndex: 'feeType',
    key: 'feeType',
    width: 130,
    options: {
      format: {
        type: 'valueList',
      },
      listName: FieldType.FEE_TYPE,
    },
  },
  {
    title: 'pmp.fees.labels.calculationType',
    dataIndex: 'calculationType',
    key: 'calculationType',
    width: 160,
    options: {
      format: {
        type: 'valueList',
      },
      listName: FieldType.CALCULATION_TYPE,
    },
  },
  {
    title: 'pmp.fees.labels.amount',
    dataIndex: 'amount',
    key: 'amount',
    width: 150,
    options: {
      format: {
        type: 'number',
      },
    },
  },
  {
    title: 'pmp.fees.labels.percentage',
    dataIndex: 'percentage',
    key: 'percentage',
    width: 150,
    options: {
      format: {
        type: 'percentage',
      },
    },
  },
  {
    title: 'pmp.fees.labels.basedOn',
    dataIndex: 'basedOn',
    key: 'basedOn',
    width: 150,
    options: {
      format: {
        type: 'valueList',
      },
      listName: FieldType.PRODUCT_FEE_BASED_ON,
    },
  },
  {
    title: 'pmp.fees.labels.baseBottomAmount',
    dataIndex: 'baseBottomAmount',
    key: 'baseBottomAmount',
    width: 160,
    options: {
      format: {
        type: 'currency',
      },
    },
  },
  {
    title: 'pmp.fees.labels.baseTopAmount',
    dataIndex: 'baseTopAmount',
    key: 'baseTopAmount',
    width: 160,
    options: {
      format: {
        type: 'currency',
      },
    },
  },
  {
    title: 'pmp.fees.labels.paymentMethods',
    dataIndex: 'paymentMethods',
    key: 'paymentMethods',
    width: 370,
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.PAYMENT_METHOD,
    },
  },
  {
    title: 'pmp.fees.labels.paymentStages',
    dataIndex: 'paymentStages',
    key: 'paymentStages',
    width: 180,
    options: {
      format: {
        type: 'multiValueList',
      },
      filter: 'select',
      listName: FieldType.PAYMENT_STAGE,
    },
  },
  {
    title: 'pmp.fees.labels.accountCode',
    dataIndex: 'accountCode',
    key: 'accountCode',
    width: 170,
    options: {
      filter: 'search',
      sorter: true,
    },
  },
  {
    title: 'pmp.fees.labels.costCentre',
    dataIndex: 'costCentre',
    key: 'costCentre',
    width: 160,
    options: {
      filter: 'search',
      sorter: true,
    },
  },
  {
    title: 'pmp.fees.labels.refundable',
    dataIndex: 'refundable',
    key: 'refundable',
    width: 150,
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
  {
    title: 'pmp.fees.labels.waivable',
    dataIndex: 'waivable',
    key: 'waivable',
    width: 150,
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
  {
    title: 'pmp.fees.labels.estimated',
    dataIndex: 'estimated',
    key: 'estimated',
    width: 150,
    options: {
      format: {
        type: 'boolean',
      },
      filter: 'select',
      listName: FieldType.YES_NO,
    },
  },
];

export const feesFlatColumns = withSourceDecimals([feesDataColumns, bands], feesFlatColumnsBase);

const feeProfilesDataColumns: TableColumns<FeeProfile> = [
  {
    title: 'pmp.fees.labels.feeProfileId',
    dataIndex: 'feeProfileId',
    key: 'feeProfileId',
    width: 230,
    options: {
      filter: 'search',
      sorter: true,
      hide: true,
    },
  },
  {
    title: 'pmp.fees.labels.feeProfileName',
    dataIndex: 'feeProfileName',
    key: 'feeProfileName',
    width: 340,
    options: {
      filter: 'search',
      sorter: true,
    },
  },
  {
    title: 'pmp.fees.labels.feeIds',
    dataIndex: 'feeIds',
    key: 'feeIds',
    width: 200,
    options: {
      filter: 'select',
      format: {
        type: 'multiValueListKey',
      },
      listName: FieldType.FEES,
      hide: true,
      hideInForm: true,
    },
  },
  {
    title: 'pmp.fees.labels.fees',
    dataIndex: 'feeIds',
    key: 'fees',
    width: 400,
    options: {
      filter: 'select',
      format: {
        type: 'multiValueList',
      },
      listName: FieldType.FEES,
    },
  },
];

const feeProfilesActionsColumn: TableColumns<FeeProfile>[number] = {
  title: 'enso.labels.actions',
  dataIndex: '',
  key: 'actions',
  width: 100,
  fixed: 'right',
};

export const feeProfilesColumns: TableColumns<FeeProfile> = [...feeProfilesDataColumns, feeProfilesActionsColumn];

export const feeProfilesListColumns: TableColumns<FeeProfile> = [
  ...feeProfilesDataColumns,
  linkedToActiveTodayColumn<FeeProfile>('pmp.fees.labels.linkedToActiveToday'),
  feeProfilesActionsColumn,
];

export const feeProfilesExportColumns: TableColumns<FeeProfile> = [
  {
    title: 'pmp.fees.labels.feeProfileId',
    dataIndex: 'feeProfileId',
    key: 'feeProfileId',
    options: {
      filter: 'search',
      sorter: true,
      hide: true,
    },
  },
  {
    title: 'pmp.fees.labels.feeProfileName',
    dataIndex: 'feeProfileName',
    key: 'feeProfileName',
    options: {
      filter: 'search',
      sorter: true,
    },
  },
  {
    title: 'pmp.fees.labels.feeIds',
    dataIndex: 'feeIds',
    key: 'feeIds',
    options: {
      filter: 'select',
      format: {
        type: 'multiValueListKey',
      },
      listName: FieldType.FEES,
      hide: true,
      hideInForm: true,
    },
  },
  {
    title: 'pmp.fees.labels.fees',
    dataIndex: 'feeIds',
    key: 'fees',
    options: {
      format: {
        type: 'multiValueList',
      },
      listName: FieldType.FEES,
    },
  },
];
